import React from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { TSetPriorityMode, TCombineModesConfig } from '../Types';
import { PRIMARY_MODES_LABEL, FALLBACK_MODES_LABEL } from '../ARSConstants';
import ModesSectionHeader from './ModesSectionHeader';
import ModeItemRow from './ModeItemRow';
import { reorderModes } from '../ModeTransformUtils';
import Divider from 'Components/DesignSystem/Divider/Divider';

type CombinedModesViewProps = {
    config: TCombineModesConfig;
    primaryModes: TSetPriorityMode[];
    fallbackModes: TSetPriorityMode[];
    onConfigChange: (config: Partial<TCombineModesConfig>) => void;
    onPrimaryModesChange: (modes: TSetPriorityMode[]) => void;
    onFallbackModesChange: (modes: TSetPriorityMode[]) => void;
    onSettingsClick: (modeKey: string) => void;
    disabled?: boolean;
    id?: string;
};

export const CombinedModesView = ({
    config,
    primaryModes,
    fallbackModes,
    onConfigChange,
    onPrimaryModesChange,
    onFallbackModesChange,
    onSettingsClick,
    disabled = false,
    id = 'combined-modes-view'
}: CombinedModesViewProps) => {
    const handlePrimaryDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const reorderedModes = reorderModes(
            primaryModes,
            result.source.index,
            result.destination.index
        );
        onPrimaryModesChange(reorderedModes);
    };

    const handleFallbackDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const reorderedModes = reorderModes(
            fallbackModes,
            result.source.index,
            result.destination.index
        );
        onFallbackModesChange(reorderedModes);
    };

    const handlePrimaryModeToggle = (index: number, checked: boolean) => {
        const updatedModes = [...primaryModes];
        updatedModes[index] = { ...updatedModes[index], active: checked };
        onPrimaryModesChange(updatedModes);
    };

    const handleFallbackModeToggle = (index: number, checked: boolean) => {
        const updatedModes = [...fallbackModes];
        updatedModes[index] = { ...updatedModes[index], active: checked };
        onFallbackModesChange(updatedModes);
    };

    const handlePrimaryTitleChange = (value: string) => {
        onConfigChange({ primary_mode_title: value });
    };

    const handleFallbackTitleChange = (value: string) => {
        onConfigChange({ fallback_mode_title: value });
    };

    const handlePrimaryMinProductChange = (value: number) => {
        onConfigChange({ primary_modes_min_product_count: value });
    };

    const handleFallbackMinProductChange = (value: number) => {
        onConfigChange({ fallback_modes_min_product_count: value });
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-4" data-testid={id}>
            <div className="tw-flex tw-flex-col" data-testid={`${id}-primary-section`}>
                <ModesSectionHeader
                    sectionLabel={PRIMARY_MODES_LABEL}
                    title={config.primary_mode_title ?? ''}
                    minProduct={config.primary_modes_min_product_count ?? 50}
                    onTitleChange={handlePrimaryTitleChange}
                    onMinProductChange={handlePrimaryMinProductChange}
                    id={`${id}-primary-header`}
                />

                <DragDropContext onDragEnd={handlePrimaryDragEnd}>
                    <Droppable droppableId="primary-modes-droppable">
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="tw-flex tw-flex-col tw-gap-2"
                            >
                                {primaryModes.map((mode, index) => (
                                    <Draggable
                                        key={mode.mode}
                                        draggableId={`primary-${mode.mode}`}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <ModeItemRow
                                                mode={mode}
                                                index={index}
                                                variant="combined"
                                                onToggle={(checked) =>
                                                    handlePrimaryModeToggle(index, checked)
                                                }
                                                onSettingsClick={() => onSettingsClick(mode.mode)}
                                                disabled={disabled && mode.mode === 'expert_curated_coseller'}
                                                provided={provided}
                                                snapshot={snapshot}
                                                id={`${id}-primary-mode`}
                                            />
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>

            <Divider orientation="horizontal" variant="solid" />

            <div className="tw-flex tw-flex-col" data-testid={`${id}-fallback-section`}>
                <ModesSectionHeader
                    sectionLabel={FALLBACK_MODES_LABEL}
                    title={config.fallback_mode_title ?? ''}
                    minProduct={config.fallback_modes_min_product_count ?? 50}
                    onTitleChange={handleFallbackTitleChange}
                    onMinProductChange={handleFallbackMinProductChange}
                    id={`${id}-fallback-header`}
                />

                <DragDropContext onDragEnd={handleFallbackDragEnd}>
                    <Droppable droppableId="fallback-modes-droppable">
                        {(provided) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="tw-flex tw-flex-col tw-gap-2"
                            >
                                {fallbackModes.map((mode, index) => (
                                    <Draggable
                                        key={mode.mode}
                                        draggableId={`fallback-${mode.mode}`}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <ModeItemRow
                                                mode={mode}
                                                index={index}
                                                variant="combined"
                                                onToggle={(checked) =>
                                                    handleFallbackModeToggle(index, checked)
                                                }
                                                onSettingsClick={() => onSettingsClick(mode.mode)}
                                                disabled={disabled && mode.mode === 'expert_curated_coseller'}
                                                provided={provided}
                                                snapshot={snapshot}
                                                id={`${id}-fallback-mode`}
                                            />
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
};

export default CombinedModesView;